import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Coffee, Check, TrendingUp, Users, ChevronRight, Star, Sparkles } from "lucide-react";
import { apiRaw } from "../lib/api";

const plans = [
    {
        id: "starter",
        name: "Starter",
        price: 49,
        priceLookup: "coffeein7_starter_monthly",
        tagline: "Perfect for a single café",
        features: [
            "Order-ahead menu with QR code",
            "10-stamp loyalty card + Beans rewards",
            "Staff kitchen dashboard",
            "Owner analytics",
            "Pay-at-counter + Stripe",
            "Apple & Google Wallet passes",
        ],
    },
    {
        id: "pro",
        name: "Pro",
        price: 79,
        popular: true,
        priceLookup: "coffeein7_pro_monthly",
        tagline: "Grow your regulars",
        features: [
            "Everything in Starter",
            "Rewards catalogue (up to 7 items)",
            "Customer data + CSV export",
            "Web push notifications",
            "Priority email support",
            "Custom accent colour",
        ],
    },
];

const testimonials = [
    {
        quote: "We went from 40 morning coffees to 90 in 3 weeks. The 10-stamp loyalty brings them back.",
        author: "Café owner",
        role: "Sydney · Founding café",
    },
];

const SaasLanding = () => {
    const nav = useNavigate();
    const [stats, setStats] = useState({ total_consumers: 0, total_tenants: 0 });

    useEffect(() => {
        apiRaw
            .get("/health")
            .then(() => {})
            .catch(() => {});
    }, []);

    return (
        <div className="min-h-screen bg-[#F9F6F0] text-[#2C1A12]">
            {/* Nav */}
            <header className="sticky top-0 z-30 bg-[#F9F6F0]/90 backdrop-blur border-b border-[#E8DCCB]/60">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="font-display font-black text-2xl leading-none">
                        Coffee<span className="text-[#FF4F18]">in7.</span>
                    </div>
                    <nav className="hidden md:flex items-center gap-6 text-sm font-semibold">
                        <a href="#features" className="hover:text-[#FF4F18] transition-colors">Features</a>
                        <a href="#pricing" className="hover:text-[#FF4F18] transition-colors">Pricing</a>
                        <a href="#how" className="hover:text-[#FF4F18] transition-colors">How it works</a>
                        <button
                            data-testid="nav-signin"
                            onClick={() => nav("/me")}
                            className="rounded-full bg-white border border-[#E8DCCB] px-4 py-2 hover:border-[#FF4F18] hover:text-[#FF4F18] transition-colors flex items-center gap-1.5"
                        >
                            ☕ Customer? Find your café
                        </button>
                    </nav>
                    <button
                        data-testid="nav-signup-cta"
                        onClick={() => nav("/signup")}
                        className="rounded-full bg-[#2C1A12] text-white px-5 py-2.5 font-display font-bold text-sm active:scale-95"
                    >
                        Start free
                    </button>
                </div>
            </header>

            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="max-w-6xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-2 gap-12 items-center">
                    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                        <div className="inline-flex items-center gap-2 rounded-full bg-white border border-[#E8DCCB] px-3 py-1.5">
                            <span className="h-2 w-2 rounded-full bg-[#1B995E] animate-pulse" />
                            <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#2C1A12]">
                                Live · Now onboarding cafés
                            </span>
                        </div>
                        <h1 className="mt-6 font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                            Turn walk-ins
                            <br />
                            into <span className="text-[#FF4F18]">regulars.</span>
                        </h1>
                        <p className="mt-6 text-lg text-[#8C7A72] max-w-md leading-relaxed">
                            Order-ahead and 10-stamp loyalty in one. No app to download. Just a QR
                            code on your counter. Cafés see 2× more repeat visits in 30 days.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <button
                                data-testid="hero-cta-signup"
                                onClick={() => nav("/signup")}
                                className="rounded-full bg-[#FF4F18] text-white px-8 py-4 font-display font-bold shadow-[0_16px_40px_rgba(255,79,24,0.32)] active:scale-95 flex items-center gap-2"
                            >
                                Start your café free
                                <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                            </button>
                            <button
                                data-testid="hero-cta-demo"
                                onClick={() => nav("/icyspicy")}
                                className="rounded-full bg-white text-[#2C1A12] border-2 border-[#E8DCCB] px-8 py-4 font-display font-bold active:scale-95"
                            >
                                See a live demo
                            </button>
                        </div>
                        <div className="mt-6 flex items-center gap-4 text-xs text-[#8C7A72]">
                            <span className="flex items-center gap-1.5">
                                <Check className="h-3.5 w-3.5 text-[#1B995E]" strokeWidth={3} /> No card required
                            </span>
                            <span className="flex items-center gap-1.5">
                                <Check className="h-3.5 w-3.5 text-[#1B995E]" strokeWidth={3} /> Live in 10 min
                            </span>
                            <span className="flex items-center gap-1.5">
                                <Check className="h-3.5 w-3.5 text-[#1B995E]" strokeWidth={3} /> Free during pilot
                            </span>
                        </div>
                    </motion.div>

                    {/* Phone mockup */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15 }}
                        className="relative"
                    >
                        <div className="mx-auto max-w-[300px] bg-[#2C1A12] rounded-[48px] p-3 shadow-[0_40px_80px_rgba(44,26,18,0.25)]">
                            <div className="rounded-[36px] bg-[#F9F6F0] p-6 aspect-[9/17] flex flex-col">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-[#8C7A72] font-bold">
                                    Demo Café
                                </div>
                                <div className="mt-2 font-display font-black text-3xl leading-tight">
                                    Coffee<span className="text-[#FF4F18]">in7.</span>
                                </div>
                                <div className="mt-6 text-[10px] uppercase tracking-[0.25em] text-[#8C7A72] font-semibold">
                                    Your mobile
                                </div>
                                <div className="mt-1 font-display font-black text-2xl border-b-2 border-[#FF4F18] pb-2">
                                    0400 000 000
                                </div>
                                <div className="mt-6 rounded-2xl bg-[#2C1A12] text-white p-4">
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#FF4F18] font-bold">
                                        Hey Sam
                                    </div>
                                    <div className="mt-1 font-display font-bold text-lg">Your usual?</div>
                                    <div className="text-white/70 text-xs">Flat White · Large</div>
                                </div>
                                <div className="flex-1" />
                                <div className="rounded-full bg-[#FF4F18] text-white text-center py-3 font-display font-bold">
                                    Reorder · $5.50
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="py-20 bg-white border-y border-[#E8DCCB]/60">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="max-w-2xl">
                        <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FF4F18]">
                            What you get
                        </div>
                        <h2 className="mt-3 font-display font-black text-4xl sm:text-5xl leading-tight">
                            Everything a café needs.
                            <br />
                            Nothing it doesn't.
                        </h2>
                    </div>
                    <div className="mt-14 grid md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: Coffee,
                                title: "Order-ahead menu",
                                body: "QR-linked ordering. Customer picks up in 7 min. Barista sees a live queue.",
                            },
                            {
                                icon: Star,
                                title: "10-stamp loyalty",
                                body: "Auto-tracked by phone number. 10th coffee free. Zero paper cards.",
                            },
                            {
                                icon: TrendingUp,
                                title: "Owner analytics",
                                body: "Top items, peak hours, best customers. Know exactly who to text next.",
                            },
                            {
                                icon: Users,
                                title: "Know your regulars",
                                body: "Understand your regular customers and build stronger relationships. Their history, tastes, and visit patterns — one tap away.",
                            },
                            {
                                icon: Sparkles,
                                title: "Beans loyalty rewards",
                                body: "Customers earn Beans on every order and redeem for free coffee, pastries or merch.",
                            },
                            {
                                icon: Check,
                                title: "Zero setup pain",
                                body: "No app to install. No POS to change. Print the QR and you're live.",
                            },
                        ].map((f) => (
                            <div
                                key={f.title}
                                className="rounded-3xl bg-[#F9F6F0] p-6 border border-[#E8DCCB]/60"
                            >
                                <div className="h-12 w-12 rounded-2xl bg-[#FF4F18]/10 flex items-center justify-center">
                                    <f.icon className="h-6 w-6 text-[#FF4F18]" strokeWidth={2.5} />
                                </div>
                                <div className="mt-4 font-display font-black text-xl">{f.title}</div>
                                <p className="mt-2 text-[#8C7A72] text-sm leading-relaxed">{f.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section id="how" className="py-20">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FF4F18]">
                        How it works
                    </div>
                    <h2 className="mt-3 font-display font-black text-4xl sm:text-5xl leading-tight max-w-2xl">
                        Live in 10 minutes. Really.
                    </h2>
                    <div className="mt-14 grid md:grid-cols-4 gap-6">
                        {[
                            { n: "1", t: "Sign up", d: "Pick a URL like coffeein7.com/yourcafé. Set an admin PIN." },
                            { n: "2", t: "Add your menu", d: "Copy in 4 coffees, 5 pastries. Prices, sizes. Done." },
                            { n: "3", t: "Print the QR", d: "Stick it on the counter. Bookmark /staff on your iPad." },
                            { n: "4", t: "Watch orders roll in", d: "Barista makes them. Customer walks in, walks out." },
                        ].map((s) => (
                            <div key={s.n} className="relative">
                                <div className="font-display font-black text-7xl text-[#E8DCCB] leading-none">
                                    {s.n}
                                </div>
                                <div className="mt-3 font-display font-bold text-xl">{s.t}</div>
                                <p className="mt-1 text-[#8C7A72] text-sm">{s.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonial */}
            <section className="py-20 bg-[#2C1A12] text-white">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <div className="text-[#FF4F18] text-xs uppercase tracking-[0.3em] font-bold">
                        From our founding café
                    </div>
                    <p className="mt-6 font-display font-bold text-2xl sm:text-3xl leading-snug">
                        "{testimonials[0].quote}"
                    </p>
                    <div className="mt-6 text-sm text-white/60">
                        <span className="font-bold text-white">{testimonials[0].author}</span> ·{" "}
                        {testimonials[0].role}
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section id="pricing" className="py-20">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-center">
                        <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FF4F18]">
                            Simple pricing
                        </div>
                        <h2 className="mt-3 font-display font-black text-4xl sm:text-5xl">
                            Pay less than one lost coffee a day.
                        </h2>
                        <p className="mt-4 text-[#8C7A72] max-w-xl mx-auto">
                            All plans include unlimited orders, unlimited customers, and Australian
                            support. Monthly billing.
                        </p>
                        <div className="mt-4 inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-[#8C7A72]">
                            <span>✓ 3-month free trial</span>
                            <span className="text-[#E8DCCB]">·</span>
                            <span>✓ Cancel anytime</span>
                            <span className="text-[#E8DCCB]">·</span>
                            <span>✓ 14-day money-back guarantee</span>
                            <span className="text-[#E8DCCB]">·</span>
                            <span>✓ No setup fees</span>
                        </div>
                    </div>
                    <div className="mt-14 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                        {plans.map((p) => (
                            <div
                                key={p.id}
                                className={`relative rounded-3xl p-8 border-2 ${
                                    p.popular
                                        ? "bg-[#2C1A12] text-white border-[#FF4F18] shadow-[0_20px_60px_rgba(255,79,24,0.2)]"
                                        : "bg-white text-[#2C1A12] border-[#E8DCCB]"
                                }`}
                                data-testid={`plan-${p.id}`}
                            >
                                {p.popular && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF4F18] text-white text-[10px] uppercase tracking-[0.2em] font-black px-3 py-1 rounded-full">
                                        Most popular
                                    </div>
                                )}
                                <div className="text-xs uppercase tracking-[0.25em] font-bold opacity-70">
                                    {p.name}
                                </div>
                                <div className="mt-4 flex items-baseline gap-1">
                                    <span className="font-display font-black text-5xl">${p.price}</span>
                                    <span className="text-sm opacity-70">/mo</span>
                                </div>
                                <div className={`text-sm mt-1 ${p.popular ? "text-white/70" : "text-[#8C7A72]"}`}>
                                    {p.tagline}
                                </div>
                                <ul className="mt-6 space-y-3">
                                    {p.features.map((f) => (
                                        <li key={f} className="flex items-start gap-2 text-sm">
                                            <Check
                                                className={`h-4 w-4 shrink-0 mt-0.5 ${p.popular ? "text-[#FF4F18]" : "text-[#1B995E]"}`}
                                                strokeWidth={3}
                                            />
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    data-testid={`plan-cta-${p.id}`}
                                    onClick={() => nav(`/signup?plan=${p.id}`)}
                                    className={`mt-8 w-full py-3 rounded-full font-display font-bold active:scale-95 ${
                                        p.popular
                                            ? "bg-[#FF4F18] text-white"
                                            : "bg-[#2C1A12] text-white"
                                    }`}
                                >
                                    Start free · 3 months
                                </button>
                            </div>
                        ))}
                    </div>
                    <div className="mt-8 text-center text-xs text-[#8C7A72]">
                        No credit card required to start · Stripe test fees pass-through during beta
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-24 bg-[#F9F6F0]">
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <h2 className="font-display font-black text-4xl sm:text-5xl leading-tight">
                        Your café's regulars are one QR code away.
                    </h2>
                    <button
                        data-testid="footer-cta-signup"
                        onClick={() => nav("/signup")}
                        className="mt-10 rounded-full bg-[#FF4F18] text-white px-10 py-5 font-display font-bold text-lg shadow-[0_20px_50px_rgba(255,79,24,0.32)] active:scale-95 inline-flex items-center gap-2"
                    >
                        Start your café free
                        <ChevronRight className="h-5 w-5" strokeWidth={2.5} />
                    </button>
                </div>
            </section>

            <footer className="border-t border-[#E8DCCB] bg-white">
                <div className="max-w-6xl mx-auto px-6 py-12">
                    <div className="grid gap-10 sm:grid-cols-3">
                        <div>
                            <div className="font-display font-black text-2xl">
                                Coffee<span className="text-[#FF4F18]">in7.</span>
                            </div>
                            <p className="mt-3 text-sm text-[#8C7A72] max-w-xs leading-relaxed">
                                One phone number. Every café. Order-ahead + loyalty built for
                                independent Australian coffee shops.
                            </p>
                        </div>
                        <div data-testid="footer-contact">
                            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF4F18]">
                                Get in touch
                            </div>
                            <p className="mt-3 text-sm text-[#8C7A72]">
                                Onboarding your café, partnerships, or just a question?
                            </p>
                            <div className="mt-3 flex flex-col gap-1">
                                <a
                                    href="mailto:admin@coffeein7.com"
                                    className="text-sm font-display font-bold text-[#2C1A12] hover:text-[#FF4F18] transition-colors"
                                    data-testid="contact-email-primary"
                                >
                                    admin@coffeein7.com
                                </a>
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#8C7A72]">
                                Explore
                            </div>
                            <div className="mt-3 flex flex-col gap-2 text-sm">
                                <a href="/signup" className="text-[#2C1A12] hover:text-[#FF4F18]">
                                    List your café
                                </a>
                                <a href="/me" className="text-[#2C1A12] hover:text-[#FF4F18]">
                                    My cafés
                                </a>
                                <a href="/icyspicy" className="text-[#2C1A12] hover:text-[#FF4F18]">
                                    See a live demo
                                </a>
                            </div>
                        </div>
                    </div>
                    <div className="mt-10 pt-6 border-t border-[#E8DCCB] flex flex-wrap justify-between gap-3 text-xs text-[#8C7A72]">
                        <div>© {new Date().getFullYear()} Coffeein7 · Made in Australia ☕</div>
                        <div className="flex flex-wrap gap-4">
                            <a href="/terms" className="hover:text-[#FF4F18]">Terms</a>
                            <a href="/privacy" className="hover:text-[#FF4F18]">Privacy</a>
                            <a href="/owner" data-testid="footer-owner-login" className="hover:text-[#FF4F18]">Café owner login</a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default SaasLanding;
